import {
  BasicBotData,
  BotStatus,
  BotType,
  DisplayResultMessage,
  LanguageNameSpaces,
  OpenMessage,
} from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { useContext, useEffect, useMemo, useState } from "react";
import { useRemoveDeselectedItems } from "@shared/utils/hooks/useRemoveDeselectedItems";
import { sharedLib } from "@shared/services/shared.service";
import { BULK_UPDATE_BOTS_MUTATION_KEY } from "@shared/features/bots/constants";
import {
  useBulkDeleteBotsModal,
  useCanManageBotType,
} from "@shared/features/bots/hooks";
import { UseQuotaModal } from "@shared/features/settings/types";
import { BOT_TYPE_TO_USAGE_TYPE } from "@shared/features/settings/constants";
import { difference, mapValues, union } from "lodash";
import { useInvalidAccount } from "@shared/features/settings/hooks";
import { getBotsInactiveAccountMessage } from "@shared/features/bots/utils";
import { MyBotsPageContext } from "@shared/features/bots/MyBotsPage.context";
import {
  useClientsData,
  useExchangesMetadata,
  useSymbolsMetadata,
} from "@shared/utils/hooks/misc";
import pluralize from "pluralize";

interface useBotsBulkActionsProps {
  openMessage?: OpenMessage;
  openActionMessage?: OpenMessage;
  navigateToBulkEdit?: (botsIds: number[]) => void;
  displayResultMessage?: DisplayResultMessage;
  useQuotaModal?: UseQuotaModal;
}

const useBotsBulkActions = ({
  openMessage: propsOpenMessage,
  openActionMessage,
  navigateToBulkEdit,
  displayResultMessage,
  useQuotaModal,
}: useBotsBulkActionsProps) => {
  const { botType, bots, unfilteredBots } = useContext(MyBotsPageContext)!;
  const { getIsFree } = useExchangesMetadata();
  const [isBulkMode, setIsBulkMode] = useState(false);
  const [botStatusSelectOpen, setBotStatusSelectOpen] = useState(false);
  const [bulkUpdateBotsModalOpen, setBulkUpdateBotsModalOpen] = useState(false);
  const [selectedBotsIDs, setSelectedBotsIDs] = useState<number[]>([]);
  const selectedBotsIDsSet = useMemo(
    () => new Set(selectedBotsIDs),
    [selectedBotsIDs]
  );
  const [selectedBotStatus, setSelectedBotStatus] = useState<
    BotStatus | undefined
  >();
  const deleteBotsModalData = useBulkDeleteBotsModal({
    botsIDs: selectedBotsIDs,
    displayResultMessage,
  });
  const { handleTriggerClick, quotaModal } =
    useQuotaModal?.({
      usageType: BOT_TYPE_TO_USAGE_TYPE[botType],
    }) || {};
  const { getInvalidAccountData } = useInvalidAccount();
  const { getAccountIsReadOnly } = useClientsData();
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const isSignalsBot = botType === BotType.SIGNAL;
  const canManageBotType = useCanManageBotType(botType);
  const { isSymbolActiveInAccount, isSymbolActiveInExchange } =
    useSymbolsMetadata();

  const bulkChangeBotsStatusStatus =
    sharedLib.sharedApi.bots.useBulkChangeBotsStatusMutation({
      fixedCacheKey: BULK_UPDATE_BOTS_MUTATION_KEY,
    })[1];
  const isActivate = selectedBotStatus !== BotStatus.ACTIVE;
  const openMessage = openActionMessage ?? propsOpenMessage;

  useEffect(() => {
    if (selectedBotsIDs.length && !isBulkMode) setIsBulkMode(true);
  }, [selectedBotsIDs]);

  const botIDToBotData = useMemo(() => {
    return (
      unfilteredBots?.reduce((acc, bot) => {
        acc[bot.id] = bot;
        return acc;
      }, {} as Record<number, BasicBotData>) || {}
    );
  }, [unfilteredBots]);

  const onSelectBots = (botsIDs: number[], isSelectAll = false) => {
    if (!canManageBotType) return;
    const readOnlyAccountBotsIds = botsIDs.filter((botId) => {
      const botData = botIDToBotData?.[botId];
      return botData?.client_id && getAccountIsReadOnly(botData.client_id);
    });
    if (!isSelectAll && readOnlyAccountBotsIds.length) {
      openMessage?.(
        t("messages.warnings.read_only_account_bot_bulk"),
        "warning"
      );
      return;
    }
    const botsIDsWithoutReadOnly = difference(botsIDs, readOnlyAccountBotsIds);
    const inactiveSymbolsTradesBotsIds = isSignalsBot
      ? []
      : botsIDsWithoutReadOnly.filter((botId) => {
          const botData = botIDToBotData?.[botId];
          const symbol = botData?.symbols?.[0];
          if (symbol) {
            if (botData?.client_id) {
              return !isSymbolActiveInAccount(symbol, botData?.client_id);
            } else if (botData?.exchanges) {
              return botData?.exchanges.every(
                (exchange) => !isSymbolActiveInExchange(symbol, exchange)
              );
            }
          }
        });
    if (inactiveSymbolsTradesBotsIds.length) {
      const numBots = inactiveSymbolsTradesBotsIds.length;
      const isSingle = numBots === 1;
      const prefix = isSingle
        ? "The selected bot's symbol is"
        : `${numBots} of the selected bot's symbols are`;
      openMessage?.(
        ` ${prefix} currently inactive in the exchange and therefore the ${pluralize(
          "bot",
          numBots
        )} cannot be updated using bulk actions.`,
        "warning"
      );
    }
    const updatedSelectedIds = difference(
      botsIDsWithoutReadOnly,
      inactiveSymbolsTradesBotsIds
    );
    setSelectedBotsIDs(updatedSelectedIds);
  };

  const exitBulkUpdate = () => {
    setIsBulkMode(false);
    onSelectBots([]);
    setSelectedBotStatus(undefined);
  };

  const botIDsSet = useMemo(
    () => new Set(unfilteredBots?.map((bot) => bot.id)),
    [unfilteredBots]
  );

  useRemoveDeselectedItems(
    selectedBotsIDs,
    botIDsSet,
    setSelectedBotsIDs,
    "bots",
    openMessage
  );

  const botIDToBot = useMemo(() => {
    return unfilteredBots?.reduce((acc, bot) => {
      acc[bot.id] = bot;
      return acc;
    }, {} as Record<number, BasicBotData>);
  }, [unfilteredBots]);

  const botIDToStatus = useMemo(() => {
    return mapValues(botIDToBot || {}, (bot) => bot.status);
  }, [botIDToBot]);

  const allowBulkChangeStatus = useMemo(() => {
    return selectedBotsIDs.every((botId) => {
      const baseStatus = botIDToStatus?.[selectedBotsIDs[0]];
      const botStatus = botIDToStatus?.[botId];
      return (
        (baseStatus === BotStatus.ACTIVE) === (botStatus === BotStatus.ACTIVE)
      );
    });
  }, [selectedBotsIDs, botIDToStatus]);

  const isInvalidAccountWarning = useMemo(() => {
    return (
      isActivate &&
      selectedBotsIDs.some((botId) => {
        const botData = botIDToBot?.[botId];
        if (botData) {
          return getInvalidAccountData(botData.client_id)?.isInvalid;
        }
      })
    );
  }, [selectedBotsIDs, botIDToBot, isActivate]);

  const isInactiveAccountWarning = useMemo(() => {
    return (
      isActivate &&
      selectedBotsIDs.some((botId) => {
        const botData = botIDToBot?.[botId];
        if (botData) {
          return getInvalidAccountData(botData.client_id)?.isInactive;
        }
      })
    );
  }, [selectedBotsIDs, botIDToBot, isActivate]);

  const allowBulkDelete = useMemo(() => {
    return selectedBotsIDs.every((botId) => {
      return botIDToStatus?.[botId] !== BotStatus.ACTIVE;
    });
  }, [selectedBotsIDs, botIDToStatus]);

  useEffect(() => {
    const firstBotStatus = botIDToStatus[selectedBotsIDs[0]];
    const shouldChangeSelectedStatus =
      selectedBotStatus &&
      allowBulkChangeStatus &&
      selectedBotStatus !== firstBotStatus;
    if (
      (selectedBotsIDs.length && !selectedBotStatus) ||
      shouldChangeSelectedStatus
    ) {
      setSelectedBotStatus(firstBotStatus);
    } else if (!selectedBotsIDs.length && selectedBotStatus) {
      setSelectedBotStatus(undefined);
    }
  }, [selectedBotsIDs, botIDToStatus, allowBulkChangeStatus]);

  const isBotSameStatus = (bot: BasicBotData, isActive: boolean) => {
    return isActive
      ? bot.status === BotStatus.ACTIVE
      : bot.status !== BotStatus.ACTIVE;
  };

  const botsWithSelectedStatus = useMemo(() => {
    return selectedBotStatus
      ? bots?.filter((bot) =>
          isBotSameStatus(bot, selectedBotStatus === BotStatus.ACTIVE)
        )
      : bots;
  }, [bots, selectedBotStatus]);

  const isAllBotsWithStatusSelected = useMemo(() => {
    return botsWithSelectedStatus?.every((bot) =>
      selectedBotsIDs.includes(bot.id)
    );
  }, [botsWithSelectedStatus, selectedBotsIDs]);

  const allBotsSameStatus = useMemo(() => {
    const isActive = bots?.[0]?.status === BotStatus.ACTIVE;
    return (
      !!bots?.length && bots?.every((bot) => isBotSameStatus(bot, isActive))
    );
  }, [bots]);

  const isAllSelected = useMemo(
    () => bots.every((bot) => selectedBotsIDsSet.has(bot.id)),
    [bots, selectedBotsIDs]
  );

  const getBotsIDs = (bots?: BasicBotData[]) =>
    bots?.map((bot) => bot.id) || [];
  const getAllIDsWithStatus = (isActive: boolean) => {
    return getBotsIDs(bots?.filter((bot) => isBotSameStatus(bot, isActive)));
  };

  const isStatusDropdownEnabled = !isAllSelected && !allBotsSameStatus;

  const onSelectUnselectAllPress = (isActive?: boolean | "all") => {
    if (isAllSelected) {
      onSelectBots(difference(selectedBotsIDs, getBotsIDs(bots)), true);
    } else if (allBotsSameStatus) {
      onSelectBots(
        union(
          getAllIDsWithStatus(bots?.[0].status === BotStatus.ACTIVE),
          selectedBotsIDs
        ),
        true
      );
    } else if (isActive === "all") {
      onSelectBots(union(selectedBotsIDs, getBotsIDs(bots)), true);
    } else if (isActive !== undefined) {
      onSelectBots(union(selectedBotsIDs, getAllIDsWithStatus(isActive)), true);
    } else {
      setBotStatusSelectOpen(true);
    }
    if (botStatusSelectOpen) {
      setBotStatusSelectOpen(false);
    }
  };

  const onBulkChangeStatusClick = async () => {
    if (!allowBulkChangeStatus) {
      openMessage?.(
        "Please select bots with the same status to perform this action.",
        "warning"
      );
      return;
    } else if (isInvalidAccountWarning || isInactiveAccountWarning) {
      openMessage?.(
        getBotsInactiveAccountMessage(
          "activate",
          isInvalidAccountWarning,
          isInactiveAccountWarning
        ),
        "warning"
      );
      return;
    }
    const isActivate = selectedBotStatus !== BotStatus.ACTIVE;
    const quotaBots = selectedBotsIDs.filter((botId) => {
      const bot = botIDToBotData[botId];
      return bot && !getIsFree(bot.exchange);
    });

    const isQuotaError =
      isActivate &&
      quotaBots.length > 0 &&
      (await handleTriggerClick?.({
        numItems: quotaBots.length,
      }));
    if (!isQuotaError) {
      setBulkUpdateBotsModalOpen(true);
    }
  };

  const onBulkDeleteClick = () => {
    if (!allowBulkDelete) {
      openMessage?.(
        "Bulk deletion is only available for deactivated bots.",
        "warning"
      );
    } else {
      deleteBotsModalData.openModal();
    }
  };

  const onBulkEditClick = () => {
    handleTriggerClick?.({
      ignoreQuotaCheck: true,
    }).then((isQuotaError) => {
      if (!isQuotaError) {
        if (isInvalidAccountWarning || isInactiveAccountWarning) {
          openMessage?.(
            getBotsInactiveAccountMessage(
              "edit",
              isInvalidAccountWarning,
              isInactiveAccountWarning
            ),
            "warning"
          );
        } else {
          navigateToBulkEdit?.(selectedBotsIDs);
        }
      }
    });
  };

  const getSelectAllOptions = () => {
    const options = [
      { text: "All Bots", onClick: () => onSelectUnselectAllPress("all") },
    ];
    const shouldShowStatusOptions =
      !isAllBotsWithStatusSelected && allowBulkChangeStatus;
    if (shouldShowStatusOptions && selectedBotStatus !== BotStatus.INACTIVE) {
      options.push({
        text: "All Bots",
        onClick: () => onSelectUnselectAllPress("all"),
      });
    }
    if (shouldShowStatusOptions && selectedBotStatus !== BotStatus.ACTIVE) {
      options.push({
        text: "All Bots",
        onClick: () => onSelectUnselectAllPress("all"),
      });
    }
    return options;
  };

  const getActionButtonsData = () => {
    if (!canManageBotType) return [];
    let actionButtonsData = [
      {
        text: isActivate ? "Activate" : "Deactivate",
        disabled:
          !allowBulkChangeStatus ||
          isInvalidAccountWarning ||
          isInactiveAccountWarning,
        onClick: onBulkChangeStatusClick,
      },
      {
        text: "Delete",
        disabled: !allowBulkChangeStatus,
        onClick: onBulkDeleteClick,
      },
    ];
    if (![BotType.GRID, BotType.SIGNAL].includes(botType)) {
      actionButtonsData.splice(1, 0, {
        text: "Edit",
        onClick: onBulkEditClick,
        disabled: isInvalidAccountWarning || isInactiveAccountWarning,
      });
    }
    if (!selectedBotsIDs.length) {
      actionButtonsData = actionButtonsData.map((buttonData) => {
        return {
          ...buttonData,
          disabled: true,
          onClick: async () =>
            openMessage?.("Select at least one bot to proceed", "warning"),
        };
      });
    }
    return actionButtonsData;
  };

  return {
    bulkUpdateBotsModalOpen,
    setBulkUpdateBotsModalOpen,
    selectedBotsIDs,
    setSelectedBotsIDs: onSelectBots,
    selectedBotStatus,
    setSelectedBotStatus,
    botsWithSelectedStatus,
    isAllSelected,
    onSelectUnselectAllPress,
    botStatusSelectOpen,
    setBotStatusSelectOpen,
    isStatusDropdownEnabled,
    isLoading:
      bulkChangeBotsStatusStatus.isLoading || deleteBotsModalData.isLoading,
    botIDToBotData,
    allowBulkChangeStatus,
    isAllBotsWithStatusSelected,
    exitBulkUpdate,
    isBulkMode,
    selectAllOptions: getSelectAllOptions(),
    actionButtonsData: getActionButtonsData(),
    deleteBotsModalData,
    quotaModal,
  };
};

export default useBotsBulkActions;
